import React from 'react';
import { useVariantOptionState } from '../context/VariantOptionContext';
import { useVariantSelectionState } from '../context/VariantSelectionContext';
import type DecisionType from '@modules/product/type/decision-type';
import type Variant from '@modules/product/type/variant';
import type Attribute from '@modules/product/type/attribute';

interface OptionDecisionType {
    ID: number;
    attributeID: number;
}

const getOptionAttributeID = (
    optionID: number,
    attributeOptionMap: Map<number, Attribute>
) => {
    return attributeOptionMap.get(optionID)?.ID || 0;
};

const findMostSatisfiedVariant = (
    variants: Variant[],
    decision: DecisionType,
    option: OptionDecisionType | null,
    sizeID: number | null,
    attributeOptionMap: Map<number, Attribute>
): Variant | null => {
    const availableVariants = variants
        .filter((v) => {
            if (option) {
                return option.ID === 0
                    ? v.Options.every(
                          (opt) => opt.AttributeID !== option.attributeID
                      )
                    : v.Options.some((o) => o.ID === option.ID);
            } else if (sizeID !== null) {
                return sizeID === 0 ? !v.Size : v.Size?.ID === sizeID;
            } else {
                return true;
            }
        })
        .sort((a, b) => {
            return a.Options.length - b.Options.length;
        });

    const perfectVariant = availableVariants
        .map((v) => {
            let score = 0;

            const neededOptionGroups = Object.keys(decision.options)
                .filter(
                    (attributeID: string) =>
                        !option || parseInt(attributeID) !== option.attributeID
                )
                .map((attributeID: string) => ({
                    attributeID: parseInt(attributeID),
                    optionID: decision.options[parseInt(attributeID)],
                }));

            neededOptionGroups.every(({ attributeID, optionID }) => {
                if (
                    optionID === 0 &&
                    !v.Options.every(
                        (opt) =>
                            getOptionAttributeID(opt.ID, attributeOptionMap) !==
                            attributeID
                    )
                ) {
                    score++;
                    return true;
                }
                if (v.Options.some((opt) => opt.ID === optionID)) {
                    score++;
                    return true;
                }
                return false;
            });
            const neededSizeID = sizeID !== null ? sizeID : decision.size;
            const sizeSatisfies = neededSizeID
                ? v.Size?.ID === neededSizeID
                : v.Size === null;

            if (sizeSatisfies) {
                score++;
            }

            return { score, variant: v };
        })
        .sort((a, b) => {
            return b.score - a.score;
        })
        .map((g) => g.variant)[0];

    return perfectVariant || (availableVariants[0] ?? null);
};

const useVariantDecision = (variants: Variant[]) => {
    const { selectedVariant, setSelectedVariant } = useVariantSelectionState();
    const { decision, setDecision, attributeOptionMap } =
        useVariantOptionState();

    const pickOption = (option: OptionDecisionType) => {
        setSelectedVariant(
            findMostSatisfiedVariant(
                variants,
                decision,
                option,
                null,
                attributeOptionMap
            )
        );
    };

    const pickSize = (sizeID: number | null) => {
        setSelectedVariant(
            findMostSatisfiedVariant(
                variants,
                decision,
                null,
                sizeID,
                attributeOptionMap
            )
        );
    };

    React.useEffect(() => {
        const decidedOptions: { [key: number]: number } = {};
        if (!selectedVariant) {
            setDecision({
                options: {},
                size: null,
            });
            return;
        }

        selectedVariant.Options.forEach((opt) => {
            decidedOptions[getOptionAttributeID(opt.ID, attributeOptionMap)] =
                opt.ID;
        });

        const newDesicsion = {
            options: decidedOptions,
            size: selectedVariant.Size?.ID || null,
        };
        setDecision(newDesicsion);
    }, [selectedVariant, setDecision, attributeOptionMap]);

    return {
        pickOption,
        pickSize,
    };
};

export { useVariantDecision };
